<template>
  <div class="course-selection-page">
    <h1 class="course-selection-page__title">Курс лучевой терапии</h1>
    <p class="course-selection-page__subtitle">
      Модуль: {{ moduleLabel(moduleId) }}. Создайте новый курс или продолжите существующий.
      В списке только курсы этого модуля.
      <router-link to="/app/calculate" class="course-selection-page__change-module">
        Сменить модуль
      </router-link>
    </p>

    <!-- Форма создания курса -->
    <div class="course-selection-page__form-card card mb-4">
      <div class="card-body">
        <h2 class="h5 card-title mb-3">Создать новый курс лучевой терапии</h2>
        <form @submit.prevent="createAndOpen" class="row g-3">
          <div class="col-12">
            <label for="course_name" class="form-label form-label-modern">Название курса лучевой терапии</label>
            <input
              id="course_name"
              v-model="form.course_name"
              type="text"
              class="form-control form-control-modern"
              data-testid="course-name-input"
              required
              placeholder="Например: Курс №1"
            />
          </div>
          <div class="col-12">
            <label for="localization" class="form-label form-label-modern">Локализация</label>
            <input
              id="localization"
              v-model="form.localization"
              type="text"
              class="form-control form-control-modern"
              data-testid="localization-input"
              required
              placeholder="Например: предстательная железа"
            />
          </div>
          <div class="col-12">
            <button
              type="submit"
              class="btn btn-primary-modern"
              data-testid="create-course-submit"
              :disabled="creating"
            >
              <i v-if="creating" class="fas fa-spinner fa-spin me-2"></i>
              <i v-else class="fas fa-plus me-2"></i>
              Создать и открыть
            </button>
          </div>
        </form>
        <p v-if="createError" class="text-danger mt-2 mb-0 small">{{ createError }}</p>
      </div>
    </div>

    <!-- Список существующих курсов -->
    <div class="course-selection-page__list">
      <h2 class="h5 mb-3">Существующие курсы лучевой терапии</h2>
      <div v-if="loading" class="course-selection-page__loading">
        <i class="fas fa-spinner fa-spin me-2"></i>Загрузка...
      </div>
      <div v-else-if="visibleCourses.length === 0" class="course-selection-page__empty">
        <p class="text-muted mb-0">В этом модуле курсов нет.</p>
      </div>
      <div v-else class="table-responsive">
        <table class="table-list">
          <thead>
            <tr>
              <th>Название курса</th>
              <th>Локализация</th>
              <th>Модуль</th>
              <th title="Суммарная доза D (Гр) / доза на сеанс d (Гр) / число сеансов n_f">D / d / n<sub>f</sub></th>
              <th>Дата</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in visibleCourses"
              :key="c.id"
              class="course-selection-page__row"
              :data-testid="`course-row-${c.id}`"
              :class="{ 'course-selection-page__row--active': isRowActionsOpen(c) }"
              @click="toggleRowActions(c)"
            >
              <td>{{ c.course_name || '—' }}</td>
              <td>{{ c.localization }}</td>
              <td>{{ moduleLabel(c.latest_mode) }}</td>
              <td class="table-list__dose-col">{{ doseCell(c) }}</td>
              <td>{{ formatDate(c.created_at) }}</td>
              <td class="course-selection-page__actions-cell">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary-modern"
                  :data-testid="`course-actions-${c.id}`"
                  :disabled="openingId === c.id || deletingId === c.id"
                  :aria-expanded="isRowActionsOpen(c) ? 'true' : 'false'"
                  aria-haspopup="menu"
                  data-course-actions-anchor
                  @click.stop="toggleRowActions(c)"
                >
                  Действия
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="activeActionsCourse"
        ref="actionsDropdown"
        class="course-selection-page__actions-dropdown"
        role="menu"
        :style="actionsDropdownStyle"
        @click.stop
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-primary-modern"
          role="menuitem"
          :disabled="openingId === activeActionsCourse.id || deletingId === activeActionsCourse.id"
          @click="continueCourse(activeActionsCourse)"
        >
          <span v-if="openingId === activeActionsCourse.id"><i class="fas fa-spinner fa-spin me-1"></i></span>
          <span v-else><i class="fas fa-folder-open me-1"></i>Продолжить курс</span>
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary-modern"
          role="menuitem"
          :data-testid="`course-history-${activeActionsCourse.id}`"
          :disabled="openingId === activeActionsCourse.id || deletingId === activeActionsCourse.id"
          @click="openCourseHistory(activeActionsCourse)"
        >
          <i class="fas fa-clock-rotate-left me-1"></i>
          История курса
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-danger-modern"
          role="menuitem"
          :data-testid="`course-delete-${activeActionsCourse.id}`"
          :disabled="openingId === activeActionsCourse.id || deletingId === activeActionsCourse.id"
          :aria-label="'Удалить курс ' + (activeActionsCourse.course_name || activeActionsCourse.localization)"
          @click="promptDeleteCourse(activeActionsCourse)"
        >
          <span v-if="deletingId === activeActionsCourse.id"><i class="fas fa-spinner fa-spin me-1"></i></span>
          <span v-else><i class="fas fa-trash-alt me-1"></i>Удалить</span>
        </button>
      </div>
    </Teleport>

    <CourseDeleteConfirmDialog
      :visible="deleteDialogVisible"
      :course-label="pendingDeleteCourseLabel"
      :localization="pendingDeleteLocalization"
      :confirming="deletingId !== null"
      :error-message="deleteError"
      @cancel="closeDeleteDialog"
      @confirm="confirmDeleteCourse"
    />

    <PopUp v-if="showPopup" :message="errorMessage" :isVisible="showPopup" @close="showPopup = false" />
  </div>
</template>

<script>
import PopUp from '../components/PopUp.vue';
import CourseDeleteConfirmDialog from '../components/course/CourseDeleteConfirmDialog.vue';
import { useCourseStore } from '../stores/courseStore';
import { HISTORY_REPORT_SCOPE_CURRENT } from '../stores/courseStore';
import { useOpenIntentStore } from '../stores/openIntentStore';
import { usePlanStore } from '../stores/planStore';
import { apiErrorMessage } from '../api/errors';
import { getCalculationModuleById, findCalculationModuleById, isCalculationModuleAvailable } from '../config/calculationModules';
import {
  COURSE_CREATE_FAILED,
  COURSE_DELETE_FAILED,
  COURSE_LIST_LOAD_FAILED,
} from '@/constants/userMessages';
import { fetchCourses, createCourse, deleteCourse, fetchHistoryList } from '../api/modules/calculatorApi';
import { formatDoseSessionColumn } from '../utils/reportTableColumns';
import { createCourseOpenIntent } from '@/utils/openIntent';
import { loadCourseWorkspaceRestore } from '@/utils/courseWorkspaceRestore';
import { resetCourseWorkflowState } from '@/utils/resetCourseWorkflowState';
import {
  buildCourseWorkspaceSessionPayload,
  persistCourseWorkspaceSession,
} from '@/utils/calculationSessionStorage';
import { isAdaptiveRtModule, normalizeModuleId } from '@/utils/adaptiveRtModule';
import { calculationModulesEqual } from '@/utils/courseResumableModule';
import {
  ensureAdaptiveRtStageSlots,
  moduleCalculationLocation,
} from '@/utils/moduleCalculationNavigation';

export default {
  name: 'CourseSelectionPage',
  components: { PopUp, CourseDeleteConfirmDialog },
  data() {
    return {
      courses: [],
      loading: true,
      creating: false,
      createError: '',
      openingId: null,
      deletingId: null,
      activeRowActionsKey: null,
      actionsDropdownStyle: {},
      deleteDialogVisible: false,
      pendingDeleteCourse: null,
      deleteError: '',
      showPopup: false,
      errorMessage: '',
      form: {
        course_name: '',
        localization: '',
      },
    };
  },
  computed: {
    pendingDeleteCourseLabel() {
      const c = this.pendingDeleteCourse;
      if (!c) return '';
      return (c.course_name || c.localization || 'курс').trim();
    },
    pendingDeleteLocalization() {
      return (this.pendingDeleteCourse?.localization || '').trim();
    },
    activeActionsCourse() {
      if (this.activeRowActionsKey == null) return null;
      return this.visibleCourses.find((course) => this.rowKey(course) === this.activeRowActionsKey) || null;
    },
    courseStore() {
      return useCourseStore();
    },
    moduleId() {
      const raw = this.$route.query.mode;
      const fromQuery = typeof raw === 'string' ? raw : '';
      const queryModule = fromQuery ? findCalculationModuleById(fromQuery) : null;
      if (queryModule && isCalculationModuleAvailable(queryModule)) return queryModule.id;
      const fromStore = this.courseStore.activeCalculationModuleId;
      const storeModule = fromStore ? findCalculationModuleById(fromStore) : null;
      if (storeModule && isCalculationModuleAvailable(storeModule)) return storeModule.id;
      return null;
    },
    visibleCourses() {
      const moduleId = this.moduleId;
      if (!moduleId) return [];
      return this.courses.filter((course) => calculationModulesEqual(course?.latest_mode, moduleId));
    },
  },
  watch: {
    activeRowActionsKey(key) {
      if (!key) {
        this.actionsDropdownStyle = {};
        return;
      }
      this.updateActionsDropdownPosition();
      this.$nextTick(() => this.updateActionsDropdownPosition());
    },
  },
  created() {
    if (!this.moduleId) {
      this.$router.replace('/app/calculate');
    }
  },
  mounted() {
    if (!this.moduleId) return;
    this.courseStore.setActiveCalculationModule(this.moduleId);
    if (this.$route.query.mode !== this.moduleId) {
      this.$router.replace({ path: '/app/calculate/course', query: { mode: this.moduleId } });
    }
    this.fetchCourses();
    document.addEventListener('click', this.handleGlobalClick);
    window.addEventListener('resize', this.scheduleActionsDropdownPosition);
    window.addEventListener('scroll', this.scheduleActionsDropdownPosition, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick);
    window.removeEventListener('resize', this.scheduleActionsDropdownPosition);
    window.removeEventListener('scroll', this.scheduleActionsDropdownPosition, true);
    if (this.actionsDropdownRaf) {
      cancelAnimationFrame(this.actionsDropdownRaf);
      this.actionsDropdownRaf = 0;
    }
  },
  methods: {
    resolveCoursePlanId(course) {
      if (!course || typeof course !== 'object') return null;
      return (
        course.latest_plan_id ||
        course.latestPlanId ||
        course.plan_id ||
        course.planId ||
        null
      );
    },
    async resolveCoursePlanIdFromHistory(courseId) {
      if (!courseId) return null;
      try {
        const data = await fetchHistoryList({ course_id: courseId });
        const rows = data?.results || [];
        if (!rows.length) return null;
        const latest = rows[0] || null;
        if (!latest) return null;
        if (latest.plan_id) return latest.plan_id;
        const items = latest.items || [];
        const firstWithPlan = items.find((item) => item?.plan_id);
        return firstWithPlan?.plan_id || null;
      } catch {
        return null;
      }
    },
    rowKey(course) {
      return course?.id ?? null;
    },
    isRowActionsOpen(course) {
      return this.activeRowActionsKey === this.rowKey(course);
    },
    toggleRowActions(course) {
      const key = this.rowKey(course);
      this.activeRowActionsKey = this.activeRowActionsKey === key ? null : key;
    },
    closeRowActions() {
      this.activeRowActionsKey = null;
    },
    getActiveActionsButton() {
      const course = this.activeActionsCourse;
      if (!course || !(this.$el instanceof Element)) return null;
      const row = this.$el.querySelector(`[data-testid="course-row-${course.id}"]`);
      return row?.querySelector('[data-course-actions-anchor]') || null;
    },
    buildActionsDropdownStyle(anchorRect, menuHeight) {
      const menuWidth = 210;
      const gap = 4;
      const pad = 8;
      const width = Math.min(menuWidth, window.innerWidth - pad * 2);
      let left = anchorRect.right - width;
      left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));

      const spaceBelow = window.innerHeight - anchorRect.bottom - gap - pad;
      const spaceAbove = anchorRect.top - gap - pad;
      const openUp = menuHeight > spaceBelow && spaceAbove > spaceBelow;
      let top = openUp ? anchorRect.top - menuHeight - gap : anchorRect.bottom + gap;
      top = Math.max(pad, Math.min(top, window.innerHeight - menuHeight - pad));

      return {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(width)}px`,
      };
    },
    updateActionsDropdownPosition() {
      const button = this.getActiveActionsButton();
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const menuEl = this.$refs.actionsDropdown;
      const measuredHeight =
        menuEl instanceof HTMLElement ? menuEl.getBoundingClientRect().height : 0;
      const menuHeight = measuredHeight || 148;
      this.actionsDropdownStyle = this.buildActionsDropdownStyle(rect, menuHeight);
    },
    scheduleActionsDropdownPosition() {
      if (!this.activeRowActionsKey) return;
      if (this.actionsDropdownRaf) cancelAnimationFrame(this.actionsDropdownRaf);
      this.actionsDropdownRaf = requestAnimationFrame(() => {
        this.actionsDropdownRaf = 0;
        this.updateActionsDropdownPosition();
      });
    },
    handleGlobalClick(event) {
      if (!this.activeRowActionsKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.course-selection-page__actions-dropdown')) return;
      if (target.closest('.course-selection-page__actions-cell > .btn')) return;
      if (target.closest('.course-selection-page__row')) return;
      this.closeRowActions();
    },
    moduleLabel(moduleId) {
      if (!moduleId) return '—';
      const calcModule = getCalculationModuleById(moduleId);
      return calcModule ? calcModule.label : moduleId;
    },
    doseCell(course) {
      if (!course || typeof course !== 'object') return '—';
      return formatDoseSessionColumn({
        totalDose: course.totalDose ?? course.total_absorbed_dose,
        n_f: course.n_f ?? course.number_of_fractions,
      });
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    async fetchCourses() {
      this.loading = true;
      try {
        const data = await fetchCourses();
        this.courses = data.courses || [];
      } catch (error) {
        this.courses = [];
        this.errorMessage = apiErrorMessage(error, COURSE_LIST_LOAD_FAILED);
        this.showPopup = true;
      } finally {
        this.loading = false;
      }
    },
    async createAndOpen() {
      const moduleId = this.moduleId;
      if (!moduleId) {
        this.$router.replace('/app/calculate');
        return;
      }
      this.createError = '';
      this.creating = true;
      try {
        const course = await createCourse({
          course_name: this.form.course_name.trim(),
          localization: this.form.localization.trim(),
          mode: moduleId,
        });
        const courseStore = this.courseStore;
        courseStore.setActiveCalculationModule(moduleId);
        resetCourseWorkflowState();
        courseStore.setCourse(course);
        courseStore.setActiveCalculationModule(moduleId);
        const openIntent = createCourseOpenIntent({
          calculationModuleId: moduleId,
          courseId: course.id,
          planId: null,
        });
        useOpenIntentStore().setIntent(openIntent);
        if (isAdaptiveRtModule(moduleId)) {
          await ensureAdaptiveRtStageSlots(course.id);
        }
        await this.$router.push(moduleCalculationLocation(moduleId));
      } catch (error) {
        const msg = apiErrorMessage(error, COURSE_CREATE_FAILED);
        this.createError = typeof msg === 'string' ? msg : JSON.stringify(msg);
      } finally {
        this.creating = false;
      }
    },
    async continueCourse(course) {
      this.openingId = course.id;
      const courseStore = this.courseStore;
      const planStore = usePlanStore();
      const modeId = this.moduleId || (course.latest_mode ? normalizeModuleId(course.latest_mode) : null);
      let planId = this.resolveCoursePlanId(course);
      if (!planId) {
        planId = await this.resolveCoursePlanIdFromHistory(course.id);
      }
      courseStore.setCourse(course);
      if (modeId) {
        courseStore.setActiveCalculationModule(modeId);
      }
      if (planId) {
        planStore.setSelectedPlanId(planId);
      }
      if (!modeId) {
        useOpenIntentStore().clearIntent();
        this.$router.push({ path: '/app/calculate' });
        this.openingId = null;
        this.activeRowActionsKey = null;
        return;
      }
      const openIntent = createCourseOpenIntent({
        calculationModuleId: modeId,
        courseId: course.id,
        planId,
      });
      useOpenIntentStore().setIntent(openIntent);
      const restorePayload = await loadCourseWorkspaceRestore({
        courseId: course.id,
        modeId,
        planId,
      });
      if (restorePayload) {
        const effectivePlanId = restorePayload.plan_id || planId;
        if (effectivePlanId) {
          planStore.setSelectedPlanId(effectivePlanId);
        }
        useOpenIntentStore().setIntent(restorePayload.openIntent || openIntent);
        persistCourseWorkspaceSession(restorePayload);
      } else {
        persistCourseWorkspaceSession(
          buildCourseWorkspaceSessionPayload({
            mode: modeId,
            planId,
            courseId: course.id,
            openIntent,
          })
        );
      }
      if (isAdaptiveRtModule(modeId)) {
        await ensureAdaptiveRtStageSlots(course.id);
      }
      this.$router.push(moduleCalculationLocation(modeId));
      this.openingId = null;
      this.activeRowActionsKey = null;
    },
    openCourseHistory(course) {
      this.openingId = course.id;
      const courseStore = useCourseStore();
      courseStore.setCourse(course);
      courseStore.setHistoryReportListScope(HISTORY_REPORT_SCOPE_CURRENT);
      this.$router.push({ path: '/app/history' });
      this.openingId = null;
      this.activeRowActionsKey = null;
    },
    promptDeleteCourse(course) {
      this.deleteError = '';
      this.pendingDeleteCourse = course;
      this.deleteDialogVisible = true;
      this.activeRowActionsKey = null;
    },
    closeDeleteDialog() {
      if (this.deletingId !== null) return;
      this.deleteDialogVisible = false;
      this.pendingDeleteCourse = null;
      this.deleteError = '';
    },
    async confirmDeleteCourse() {
      const course = this.pendingDeleteCourse;
      if (!course || this.deletingId !== null) return;

      this.deleteError = '';
      this.deletingId = course.id;
      try {
        await deleteCourse(course.id);
        const courseStore = useCourseStore();
        if (courseStore.selectedCourseId === course.id) {
          courseStore.clearCourse();
        }
        this.deleteDialogVisible = false;
        this.pendingDeleteCourse = null;
        await this.fetchCourses();
        this.activeRowActionsKey = null;
      } catch (error) {
        this.deleteError = apiErrorMessage(error, COURSE_DELETE_FAILED);
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style scoped>
.course-selection-page {
  max-width: 900px;
  margin: 0 auto;
}

.course-selection-page__title {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--tcp-color-text-primary);
}

.course-selection-page__subtitle {
  color: var(--tcp-color-text-muted);
  margin-bottom: 2rem;
}

.course-selection-page__change-module {
  display: inline-block;
  margin-left: 0.35rem;
  color: var(--tcp-color-brand-start);
  font-weight: 600;
  text-decoration: none;
}

.course-selection-page__change-module:hover {
  text-decoration: underline;
}

.course-selection-page__form-card .card-body {
  padding: 1.25rem;
}

.course-selection-page__loading {
  color: var(--tcp-color-text-muted);
}

.course-selection-page__actions-cell {
  white-space: nowrap;
}

.course-selection-page__row {
  cursor: pointer;
}

.course-selection-page__row:hover td {
  background: color-mix(in srgb, var(--tcp-color-brand-start) 5%, white);
}

.course-selection-page__row--active td {
  background: color-mix(in srgb, var(--tcp-color-brand-start) 7%, white);
}
</style>

<style>
.course-selection-page__actions-dropdown {
  position: fixed;
  z-index: 1100;
  min-width: 210px;
  padding: 0.4rem;
  border: 1px solid var(--tcp-color-border);
  border-radius: 10px;
  background: var(--tcp-color-surface);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.course-selection-page__actions-dropdown .btn {
  text-align: left;
}
</style>
